import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type TSidebarContext = {
  /** True when the sidebar is shrunk to icon-only mode. */
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  toggleSidebar: () => void;
  collapseSidebar: () => void;
  expandSidebar: () => void;
};

const SidebarContext = createContext<TSidebarContext | null>(null);

/**
 * Session-only sidebar collapse state. Lives above the layouts so any
 * action anywhere in the app (outside the sidebar) can collapse/expand it
 * via `useSidebarContext()`. Not persisted — resets on reload.
 */
export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  const [collapsed, setCollapsed] = useState(false);

  const toggleSidebar = useCallback(() => setCollapsed((c) => !c), []);
  const collapseSidebar = useCallback(() => setCollapsed(true), []);
  const expandSidebar = useCallback(() => setCollapsed(false), []);

  const value = useMemo(
    () => ({
      collapsed,
      setCollapsed,
      toggleSidebar,
      collapseSidebar,
      expandSidebar,
    }),
    [collapsed, toggleSidebar, collapseSidebar, expandSidebar],
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};

export const useSidebarContext = (): TSidebarContext => {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error("useSidebarContext must be used inside SidebarProvider");
  }
  return ctx;
};
